import React, {useCallback, useMemo} from 'react'
import {Text, Link} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import useMultiSite from '../../../hooks/use-multi-site'
import {HOME_HREF} from '../../../constants'
import textStyles from '../../../theme/foundations/textStyles'
import {trackCountrySelection} from '../../../tracking/analytics'

const capitalize = (string) => string.charAt(0).toUpperCase() + string.slice(1)
const TW_DISPLAY_NAME = '台灣 - 大中華地區'
const TW_ENGLISH_NAME = 'Taiwan - Greater China'

export const IntlDisplayName = ({
    locale = '',
    languageCode = 'en',
    continentName = 'Unknown',
    countryCode = 'US',
    type = 'region',
    siteId,
    asLink = false,
    linkLocale,
    suffix = '',
    textStyle = '',
    currentLocation = {},
    englishCountryName = '',
    ...props
}) => {
    const {buildUrl} = useMultiSite()

    const updatedLocale = useMemo(
        () => locale || languageCode + '-' + countryCode,
        [countryCode, languageCode, locale]
    )

    const regionNames = useMemo(
        () =>
            new Intl.DisplayNames([languageCode || updatedLocale.slice(0, 2)], {
                type: type || 'region'
            }),
        [languageCode, type, updatedLocale]
    )

    const code = useMemo(
        () =>
            type === 'language'
                ? languageCode || updatedLocale.slice(0, 2)
                : countryCode || updatedLocale.slice(-2),
        [countryCode, languageCode, type, updatedLocale]
    )

    const getCountryName = useCallback(
        (code, useEnglish = false) => {
            if (code === 'TW') {
                return useEnglish ? TW_ENGLISH_NAME : TW_DISPLAY_NAME
            }
            return useEnglish ? englishCountryName : regionNames.of(code) || 'Unknown'
        },
        [englishCountryName, regionNames]
    )

    const trackCountrySelect = () => {
        const fromLocation = {
            continent: currentLocation?.continent,
            country: capitalize(getCountryName(currentLocation?.country))
        }
        const toLocation = {
            continent: continentName,
            country: capitalize(getCountryName(code))
        }
        trackCountrySelection(fromLocation, toLocation)
    }

    const href = useMemo(
        () => buildUrl(HOME_HREF, siteId, linkLocale || updatedLocale),
        [buildUrl, siteId, linkLocale, updatedLocale]
    )

    const displayName = useMemo(
        () => capitalize(getCountryName(code, languageCode === 'en' && englishCountryName)),
        [code, languageCode, englishCountryName, getCountryName]
    )

    if (asLink) {
        return (
            <Link
                href={href}
                minWidth="auto"
                whiteSpace="nowrap"
                pb={0}
                {...(textStyle && textStyles[textStyle])}
                {...props}
                onClick={trackCountrySelect}
            >
                {displayName}
                {suffix}
            </Link>
        )
    }

    return (
        <Text whiteSpace="nowrap" textStyle={textStyle} {...props}>
            {displayName}
            {suffix}
        </Text>
    )
}

IntlDisplayName.propTypes = {
    locale: PropTypes.string,
    languageCode: PropTypes.string,
    continentName: PropTypes.string,
    countryCode: PropTypes.string,
    type: PropTypes.string,
    siteId: PropTypes.any,
    asLink: PropTypes.bool,
    linkLocale: PropTypes.string,
    suffix: PropTypes.string,
    textStyle: PropTypes.string,
    currentLocation: PropTypes.shape({
        continent: PropTypes.string,
        country: PropTypes.string
    }),
    englishCountryName: PropTypes.string
}

export default IntlDisplayName
